// =============================================================================
// subpageGenerator — generuje HTML jednej podstrony (lekki LLM, wzorzec author page).
// Bez SERP/KG/Fan-Out. Zapis html + status='ready' do unique_subpages.
// =============================================================================

import { supabase } from "@/integrations/supabase/client";
import { loadPrompt } from "@/services/llm/promptLoader";
import { getLLMProvider } from "@/services/llm";
import { compilePromptSafe } from "@/services/contentEngine/promptCompiler";
import { logPublic, logError } from "@/services/contentEngine/contentLogger";
import { fetchMapSubpageContext } from "./subpageContext";
import type { UniqueSubpage } from "@/types/uniquePages";

export interface SubpageGenResult {
  status: "ok" | "skipped" | "error";
  reason?: string;
}

/** Usuwa ewentualne ```html ... ``` fences i znaczniki dokumentu. */
export function cleanHtmlOutput(raw: string): string {
  let t = (raw || "").trim();
  const fence = t.match(/```(?:html)?\s*([\s\S]*?)\s*```/i);
  if (fence) t = fence[1].trim();
  // usuń ewentualne <!DOCTYPE>/<html>/<head>/<body> wrappery
  t = t.replace(/<!DOCTYPE[^>]*>/gi, "").replace(/<\/?(html|head|body)[^>]*>/gi, "").trim();
  return t;
}

export async function generateSubpage(subpageId: string): Promise<SubpageGenResult> {
  try {
    const { data: sp } = await (supabase as any)
      .from("unique_subpages")
      .select("id, map_id, kind, title, topic")
      .eq("id", subpageId)
      .maybeSingle();
    if (!sp) return { status: "error", reason: "Nie znaleziono podstrony" };

    const ctx = await fetchMapSubpageContext(sp.map_id);
    if (!ctx) return { status: "error", reason: "Nie znaleziono mapy" };

    const prompt = await loadPrompt("unique_subpage_generation" as any);
    if (!prompt) return { status: "error", reason: "Brak promptu unique_subpage_generation" };

    await (supabase as any).from("unique_subpages").update({ status: "generating" }).eq("id", subpageId);

    const compiled = compilePromptSafe(
      prompt.template,
      {
        service_name: ctx.serviceName,
        author_name: ctx.authorName,
        kind: (sp as UniqueSubpage).kind || "",
        title: sp.title,
        topic: sp.topic || "",
        macro_statement: ctx.macroStatement,
        language: ctx.language,
      },
      "unique_subpage_generation",
      "",
    );
    if (!compiled) {
      await (supabase as any).from("unique_subpages").update({ status: "draft_topic" }).eq("id", subpageId);
      return { status: "error", reason: "Kompilacja promptu nie powiodła się" };
    }

    let html = "";
    try {
      const provider = getLLMProvider((prompt.provider || "openai") as "openai" | "claude");
      const resp = await provider.call(compiled, {
        model: prompt.modelId || "gpt-4.1-mini",
        temperature: prompt.temperature ?? 0.6,
        step: "unique_subpage_generation",
        mapId: sp.map_id,
      });
      html = cleanHtmlOutput(resp.text || "");
    } catch (e) {
      await (supabase as any).from("unique_subpages").update({ status: "draft_topic" }).eq("id", subpageId);
      logError("UNIQUE-GEN", `LLM padł dla subpage=${subpageId.slice(0, 8)}`, e);
      return { status: "error", reason: e instanceof Error ? e.message : "LLM error" };
    }

    if (html.length < 40) {
      await (supabase as any).from("unique_subpages").update({ status: "draft_topic" }).eq("id", subpageId);
      return { status: "skipped", reason: "LLM zwrócił zbyt krótki HTML" };
    }

    const { error } = await (supabase as any)
      .from("unique_subpages")
      .update({ html, status: "ready", updated_at: new Date().toISOString() })
      .eq("id", subpageId);
    if (error) return { status: "error", reason: error.message };

    logPublic("UNIQUE-GEN", `Podstrona gotowa: ${sp.title} (${html.length} zn.)`);
    return { status: "ok" };
  } catch (e) {
    logError("UNIQUE-GEN", `generateSubpage error subpage=${subpageId.slice(0, 8)}`, e);
    return { status: "error", reason: e instanceof Error ? e.message : "wyjątek" };
  }
}
